import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../hooks';

import { ProspectActions } from '../actions/prospect/prospect.actions';

import type { Prospect } from '../interfaces/employee/prospect.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import { initialsFromName } from './lib/mandate-helpers';
import { useTableSort, SortableTh } from './lib/use-table-sort';

// =============================================================================
//  Prospects (recruitment) — v3
//
//  HR-only throughout: unlike payslips or timesheets there is no "own
//  records" notion here — a candidate does not belong to the person viewing
//  them — so the whole surface is gated, on the route and in the API
//  (ProspectsController requires an HR role for every action).
//
//  The headline action is Convert, which promotes a candidate to an employee
//  and removes the prospect. That is destructive and not obviously
//  reversible, so it asks for confirmation inline on the row rather than
//  firing on a single click.
//
//  Add / edit / documents still open the legacy screens; the multi-document
//  upload flow has not been ported.
// =============================================================================

const nameOf = (p: Prospect): string =>
  p.full_name?.trim() ||
  `${(p.first_name || '').trim()} ${(p.last_name || '').trim()}`.trim() ||
  p.email ||
  `Prospect #${p.id}`;

const documentCount = (p: Prospect): number =>
  Array.isArray(p.documents) ? p.documents.length : 0;

const ProspectsListV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const prospects: Prospect[] =
    useAppSelector(({ prospect_store }) => prospect_store.prospects) || [];

  const [loaded, setLoaded] = useState(prospects.length > 0);
  const [search, setSearch] = useState('');
  // Two-step confirm for the destructive convert.
  const [confirmingId, setConfirmingId] = useState<string | number | null>(null);
  const [convertingId, setConvertingId] = useState<string | number | null>(null);

  useEffect(() => {
    dispatch(ProspectActions.loadProspects(() => setLoaded(true)));
  }, [dispatch]);

  const handleConvert = (p: Prospect) => {
    if (!p.id || convertingId) return;
    setConvertingId(p.id);
    dispatch(
      ProspectActions.convertProspect(p.id, p, () => {
        setConvertingId(null);
        setConfirmingId(null);
        // The prospect no longer exists after a successful convert, so refresh
        // rather than leaving a stale row on screen.
        dispatch(ProspectActions.loadProspects(() => setLoaded(true)));
      })
    );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return prospects;
    return prospects.filter((p) =>
      `${nameOf(p)} ${p.email ?? ''} ${p.role ?? ''}`.toLowerCase().includes(q)
    );
  }, [prospects, search]);

  const SORT_ACCESSORS = useMemo(
    () => ({
      candidate: (p: Prospect) => nameOf(p),
      role: (p: Prospect) => p.role,
      email: (p: Prospect) => p.email,
      documents: (p: Prospect) => documentCount(p)
    }),
    []
  );
  const { sorted, sort, toggle } = useTableSort(filtered, SORT_ACCESSORS);

  const withDocs = useMemo(() => prospects.filter((p) => documentCount(p) > 0).length, [prospects]);

  const subLine = () => {
    if (!loaded && prospects.length === 0) return 'Loading…';
    return [
      filtered.length === prospects.length
        ? `${prospects.length} candidate${prospects.length === 1 ? '' : 's'}`
        : `${filtered.length} of ${prospects.length} candidates`,
      withDocs > 0 ? `${withDocs} with documents` : undefined
    ]
      .filter(Boolean)
      .join(' · ');
  };

  return (
    <>
      <Topbar crumbs={['Human Resources', 'Prospects']} />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Recruitment</div>
            <h1 className="page-title">Prospects</h1>
            <div className="page-sub">{subLine()}</div>
          </div>
          <div className="page-tools">
            <label className="search">
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder="Search candidates…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <button
              className="btn primary"
              type="button"
              onClick={() => navigate('/human-resources/prospects/add')}
              title="Opens the legacy prospect form"
            >
              <Icon name="plus" size={14} />
              Add prospect
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <span className="muted">
              {!loaded && prospects.length === 0
                ? 'Loading prospects…'
                : prospects.length === 0
                  ? 'No prospects in the pipeline.'
                  : 'No candidates match this search.'}
            </span>
          </div>
        ) : (
          <div className="card flush">
            <table className="tbl">
              <thead>
                <tr>
                  <SortableTh label="Candidate" sortKey="candidate" sort={sort} onToggle={toggle} />
                  <SortableTh label="Role" sortKey="role" sort={sort} onToggle={toggle} />
                  <SortableTh label="Email" sortKey="email" sort={sort} onToggle={toggle} />
                  <SortableTh label="Documents" sortKey="documents" sort={sort} onToggle={toggle} />
                  <th className="actions-cell" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => {
                  const docs = documentCount(p);
                  const confirming = confirmingId === p.id;
                  const busy = convertingId === p.id;
                  return (
                    <tr key={p.id}>
                      <td className="strong">
                        <div className="team-row">
                          <div className="av">{initialsFromName(nameOf(p))}</div>
                          <div className="who">
                            <span className="name">{nameOf(p)}</span>
                            {p.id_number && (
                              <span className="role mono" style={{ fontSize: 11 }}>
                                {p.id_number}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{p.role || <span className="muted">Not specified</span>}</td>
                      <td>{p.email || '—'}</td>
                      <td>
                        {docs > 0 ? (
                          <span className="chip muted">
                            {docs} file{docs === 1 ? '' : 's'}
                          </span>
                        ) : (
                          <span className="muted">None</span>
                        )}
                      </td>
                      <td className="actions-cell">
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            className="btn ghost"
                            type="button"
                            onClick={() => navigate(`/human-resources/prospects/${p.id}`)}
                            title="Opens the legacy prospect record"
                          >
                            Open
                          </button>
                          {confirming ? (
                            <>
                              <button
                                className="btn ghost"
                                type="button"
                                disabled={busy}
                                onClick={() => setConfirmingId(null)}
                              >
                                Cancel
                              </button>
                              <button
                                className="btn primary"
                                type="button"
                                disabled={busy}
                                onClick={() => handleConvert(p)}
                                title="Creates an employee record and removes this prospect"
                              >
                                {busy ? 'Converting…' : 'Confirm hire'}
                              </button>
                            </>
                          ) : (
                            <button
                              className="btn"
                              type="button"
                              onClick={() => p.id && setConfirmingId(p.id)}
                              title="Promote this candidate to an employee"
                            >
                              Convert
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {confirmingId !== null && (
          <div className="muted" style={{ marginTop: 12, fontSize: 12.5 }}>
            Converting creates an employee record, moves the candidate&apos;s documents across, and
            removes them from the pipeline. The new employee signs in using &ldquo;Forgot
            password&rdquo;.
          </div>
        )}
      </div>
    </>
  );
};

export default ProspectsListV3;
